import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Image, LayoutGrid, Printer, ScanLine, ShieldAlert, ShieldCheck } from "lucide-react";
import { Brand } from "../components/Brand";
import { SpendGateBanner } from "../components/SpendGateBanner";
import { Button } from "../components/ui/button";
import adminApi from "../lib/adminApi";
import { isEmbedded } from "../lib/shopifyAdmin";

const POINTS = [
  { icon: Image, title: "Photo in, duck out", text: "A customer uploads a portrait; Gemini renders them as a Zero Ducks Given figurine and approves the side views." },
  { icon: Printer, title: "Print-ready files", text: "Every duck becomes a multi-colour Bambu .3MF with a flat base, plus a textured GLB and an A6 / Rollo Duck Passport." },
  { icon: ScanLine, title: "Orders land here", text: "“Print this for me” drops the duck in this store's cart with its link, height and filament list attached — you print exactly what they saw." },
];

// Landing panel for the Duckify app tile inside Shopify admin (set the app's App URL to /shop-app).
export default function ShopAppPage() {
  const [waiting, setWaiting] = useState(null);

  useEffect(() => {
    if (!isEmbedded()) return; // the badge needs a Shopify session token, only available inside admin
    adminApi.get("/stats").then(({ data }) => setWaiting(data.waiting)).catch(() => {});
  }, []);

  return (
    <div className="App min-h-screen bg-dots">
      <main className="max-w-3xl mx-auto px-6 py-14">
        <Brand />
        <div className="mt-6">
          <SpendGateBanner />
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-8">Duckify3D</h1>
        <p className="text-base md:text-lg text-muted-foreground mt-4 max-w-2xl">
          The custom-duck studio behind <span className="font-semibold text-foreground">Zero Ducks Given</span>: it turns a customer's photo into a
          3D-printable rubber-duck bust, then hands the order straight to this store.
        </p>

        <ul className="mt-12 space-y-6">
          {POINTS.map(({ icon: Icon, title, text }) => (
            <li key={title} data-testid={`shop-app-point-${title.split(" ")[0].toLowerCase()}`} className="flex gap-4">
              <span className="grid place-items-center h-11 w-11 shrink-0 rounded-2xl bg-primary/15 text-primary">
                <Icon className="h-5 w-5" />
              </span>
              <div>
                <p className="font-heading font-bold text-base md:text-lg">{title}</p>
                <p className="text-sm text-muted-foreground mt-1">{text}</p>
              </div>
            </li>
          ))}
        </ul>

        <div className="mt-12 flex flex-wrap gap-3">
          <Button asChild className="rounded-full h-12 px-6 font-bold gap-2 duck-shadow">
            <Link to="/shop-app/orders" data-testid="shop-app-orders-link">
              Custom duck orders
              {waiting > 0 && (
                <span data-testid="orders-waiting-badge" className="inline-grid place-items-center min-w-6 h-6 px-1.5 rounded-full bg-white/25 text-xs font-extrabold">
                  {waiting}
                </span>
              )}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
          <Button asChild variant="outline" className="rounded-full h-12 px-6 font-bold gap-2">
            <Link to="/shop-app/queue" data-testid="shop-app-queue-link">
              Print queue <LayoutGrid className="h-4 w-4" />
            </Link>
          </Button>
          <Button asChild variant="outline" className="rounded-full h-12 px-6 font-bold gap-2">
            <Link to="/shop-app/spend-gate" data-testid="shop-app-spend-gate-link">
              Spend gate <ShieldCheck className="h-4 w-4" />
            </Link>
          </Button>
          <Button asChild variant="outline" className="rounded-full h-12 px-6 font-bold gap-2">
            <Link to="/shop-app/held" data-testid="shop-app-held-link">
              Held ducks <ShieldAlert className="h-4 w-4" />
            </Link>
          </Button>
          <Button asChild variant="outline" className="rounded-full h-12 px-6 font-bold gap-2">
            <a data-testid="shop-app-studio-link" href={`${window.location.origin}/`} target="_blank" rel="noopener noreferrer">
              Open the duck studio <ArrowRight className="h-4 w-4" />
            </a>
          </Button>
          <Button asChild variant="outline" className="rounded-full h-12 px-6 font-bold">
            <a data-testid="shop-app-library-link" href="/library" target="_top" rel="noopener">
              Shop sync &amp; library admin
            </a>
          </Button>
        </div>

        <p className="text-xs text-muted-foreground mt-10">
          Printed wall, passports and reorder links live in the studio.{" "}
          <a href={`${window.location.origin}/`} target="_blank" rel="noopener noreferrer" data-testid="shop-app-home-link" className="underline">
            duckify.zeroducksgiven.shop
          </a>
        </p>
      </main>
    </div>
  );
}
